import { Component, Input, OnInit } from '@angular/core';
import { LambdaService } from 'src/app/lambda.service';
import { SettingsService } from 'src/app/settings.service';
import { ThemingService } from 'src/app/theming.service';
import {
  extractShopCompanies,
  ShopCompany,
} from './shop-company.models';

@Component({
  selector: 'app-shop-companies',
  templateUrl: './shop-companies.component.html',
  styleUrls: ['./shop-companies.component.scss'],
})
export class ShopCompaniesComponent implements OnInit {
  @Input() embedded = false;
  companies: ShopCompany[] = [];
  loading = false;
  errorMessage = '';
  deleteMessage = '';
  searchString = '';
  modalDelete = false;
  companyToDelete: ShopCompany | null = null;

  constructor(
    public themingService: ThemingService,
    private lambdaService: LambdaService,
    private settingsService: SettingsService
  ) {}

  ngOnInit(): void {
    this.settingsService.setListingShopCompanies();
    this.loadCompanies();
  }

  async loadCompanies(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';
    try {
      const response: any = await this.lambdaService.getShopCompanies(
        this.themingService.shopId
      );
      this.companies = extractShopCompanies(response);
    } catch (error) {
      console.error('Error loading shop companies:', error);
      this.errorMessage = 'SHOP_COMPANIES_LOAD_ERROR';
    } finally {
      this.loading = false;
    }
  }

  get filteredCompanies(): ShopCompany[] {
    const search = this.searchString.trim().toLowerCase();
    if (!search) {
      return this.companies;
    }
    return this.companies.filter((company) =>
      [
        company.companyName,
        company.companyLegalName,
        company.companyPhone,
        company.companyWebsiteUrl,
      ].some((value) => String(value || '').toLowerCase().includes(search))
    );
  }

  createCompany(): void {
    this.settingsService.setCreatingShopCompany();
  }

  editCompany(shopCompanyId: number): void {
    this.settingsService.setEditingShopCompany(String(shopCompanyId));
  }

  async deleteCompany(confirmed: boolean): Promise<void> {
    this.modalDelete = false;
    if (!confirmed || !this.companyToDelete) {
      return;
    }

    const company = this.companyToDelete;
    this.loading = true;
    this.deleteMessage = '';
    try {
      const response: any = await this.lambdaService.deleteShopCompany(
        this.themingService.shopId,
        company.shopCompanyId
      );
      const statusCode = response?.statusCode ?? response?.status;
      if (statusCode === 409) {
        this.deleteMessage = 'SHOP_COMPANY_DELETE_CONFLICT';
        return;
      }
      if (statusCode && statusCode >= 400) {
        this.deleteMessage = 'SHOP_COMPANY_DELETE_ERROR';
        return;
      }
      await this.loadCompanies();
    } catch (error: any) {
      console.error('Error deleting shop company:', error);
      this.deleteMessage =
        error?.status === 409 || error?.statusCode === 409
          ? 'SHOP_COMPANY_DELETE_CONFLICT'
          : 'SHOP_COMPANY_DELETE_ERROR';
    } finally {
      this.loading = false;
    }
  }
}
